import { type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { ChevronDown, LogOut, User, ShieldCheck } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

// ─── Nav definition ───────────────────────────────────────────────────────────

type NavItem =
  | { title: string; url: string; items?: never }
  | { title: string; url?: string; items: { title: string; url: string }[] };

const adminNavItems: NavItem[] = [
  { title: "Dashboard",       url: "/" },
  { title: "User Management", url: "/users" },
  {
    title: "Reports",
    items: [
      { title: "Sales Report",   url: "/reports/sales" },
      { title: "Activity Logs",  url: "/reports/activity-logs" },
      { title: "Export Users",   url: "/export/users" },
      { title: "Server History", url: "/server-history" },
      { title: "AI Usage",       url: "/ai-usage" },
      { title: "AI Chat Feedback", url: "/ai-chat-feedback" },
    ],
  },
  {
    title: "Payments",
    items: [
      { title: "Subscription Plans", url: "/subscription-plans" },
      { title: "Payment Settings",   url: "/payment-settings" },
      { title: "Vouchers",           url: "/vouchers" },
    ],
  },
  { title: "Campaigns",     url: "/campaigns" },
  { title: "Subscriptions", url: "/subscriptions" },
  { title: "AI Management", url: "/ai-management" },
  {
    title: "Messaging",
    items: [
      { title: "Announcements",       url: "/announcements" },
      { title: "Email Management",    url: "/email-management" },
      { title: "Email Campaigns",     url: "/email-campaigns" },
      { title: "Email Sequences",     url: "/email-sequences" },
      { title: "Email Templates",     url: "/email-templates" },
      { title: "Inbox",               url: "/inbox" },
      { title: "Notification Emails", url: "/messaging/notification-emails" },
    ],
  },
  { title: "Helpdesk",         url: "/helpdesk" },
  {
    title: "Content",
    items: [
      { title: "ChefAiQ Library", url: "/chefaiq-library" },
      { title: "Library Recipes", url: "/chefaiq-library/recipes" },
      { title: "Starter Kit",     url: "/starter-pack" },
    ],
  },
  { title: "Landing Page",     url: "/landing-page" },
  { title: "User Guide Media", url: "/user-guide-media" },
  { title: "Integrations",     url: "/integrations" },
  {
    title: "Workspace",
    items: [
      { title: "Workspace Settings",  url: "/workspace-settings" },
      { title: "Team Settings",       url: "/team-settings" },
      { title: "Roles & Permissions", url: "/workspace-roles" },
    ],
  },
  { title: "Impersonate",      url: "/impersonate" },
  { title: "Settings",         url: "/settings" },
  { title: "Density Table",    url: "/density-references" },
  { title: "Yield % Table",    url: "/yield-references" },
];

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function AdminSidebar() {
  const [location] = useLocation();

  return (
    <Sidebar>
      {/* Logo */}
      <SidebarHeader className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-2.5">
          <img src="/ChefAIQLogo_MAIN.png" alt="ChefAIQ" className="w-8 h-8 rounded-lg object-contain shrink-0" />
          <div>
            <p className="font-bold text-sm leading-tight">ChefAIQ</p>
            <p className="text-[10px] text-muted-foreground leading-tight">Admin Panel</p>
          </div>
        </div>
      </SidebarHeader>

      {/* Nav */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminNavItems.map((item) => {
                if (item.items) {
                  const childActive = item.items.some((c) => location === c.url);
                  return (
                    <Collapsible key={item.title} defaultOpen={childActive}>
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton data-active={childActive}>
                            <span>{item.title}</span>
                            <ChevronDown className="ml-auto h-4 w-4 transition-transform" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items.map((sub) => (
                              <SidebarMenuSubItem key={sub.title}>
                                <SidebarMenuSubButton asChild data-active={location === sub.url}>
                                  <Link href={sub.url}>
                                    <span>{sub.title}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                }

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild data-active={location === item.url}>
                      <Link href={item.url!}>
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

// ─── Top navbar ───────────────────────────────────────────────────────────────

function TopNav() {
  const { user, logout } = useAuth();

  const initials = [user?.firstName?.[0], user?.lastName?.[0]]
    .filter(Boolean)
    .join("")
    .toUpperCase() || "A";

  return (
    <header
      className="sticky top-0 z-50 flex items-center justify-between border-b border-primary/20 px-3 py-2 bg-background shrink-0 gap-2"
      style={{ borderTopWidth: "3px", borderTopColor: "hsl(142 76% 36%)" }}
    >
      {/* Left: sidebar toggle + logo */}
      <div className="flex items-center gap-2">
        <SidebarTrigger />
        <div className="flex items-center gap-2">
          <img src="/ChefAIQLogo_MAIN.png" alt="ChefAIQ" className="w-7 h-7 rounded-lg object-contain" />
          <span className="font-bold text-sm hidden sm:inline">ChefAIQ</span>
          <span className="text-xs text-muted-foreground hidden sm:inline flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 inline" /> Admin
          </span>
        </div>
      </div>

      {/* Right: user dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2 h-auto p-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:flex flex-col items-start min-w-0">
              <span className="text-sm font-medium truncate">
                {user?.firstName ? `${user.firstName} ${user.lastName ?? ""}`.trim() : user?.email}
              </span>
              <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Admin Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-muted-foreground cursor-default" disabled>
            <User className="mr-2 h-4 w-4" />
            <span>{user?.email}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={logout}
            className="text-destructive focus:text-destructive cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export function AdminLayout({ children }: { children: ReactNode }) {
  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <AdminSidebar />

        <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
          <TopNav />
          <main className="flex-1 overflow-y-auto p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
